import { css, Global } from '@emotion/react'
import type { Theme } from '@mui/material/styles'
import {
  createTheme,
  palette,
  ScopedCssBaseline,
  StyledEngineProvider,
  scopedCssBaselineClasses,
  ThemeProvider,
  themeConfig
} from '@phocassoftware/shared-components'
import { createDefaultDashboardPalette, type DashboardThemeMode } from '@phocassoftware/visual-config-builder/theme'
import type { CSSProperties, ReactNode } from 'react'
import { ThemeProvider as AppThemeProvider, useTheme } from './ThemeContext'
import './theme.d.ts'

interface StylesProviderProps {
  children: ReactNode
  mode?: DashboardThemeMode
  includeGlobalStyles?: boolean
}

interface DashboardCssVariableStyle extends CSSProperties {
  '--dashboard-text-primary': string
  '--dashboard-text-secondary': string
  '--dashboard-icon-default': string
  '--dashboard-icon-active': string
  '--dashboard-border': string
  '--dashboard-control-background': string
  '--dashboard-control-disabled-background': string
  '--dashboard-menu-background': string
  '--dashboard-menu-hover-background': string
  '--dashboard-slider-background': string
}

type DashboardCssVariableMap = Omit<DashboardCssVariableStyle, keyof CSSProperties>

const createDashboardCssVariableMap = (theme: Theme): DashboardCssVariableMap => ({
  '--dashboard-text-primary': theme.palette.dashboard.text.primary,
  '--dashboard-text-secondary': theme.palette.dashboard.text.secondary,
  '--dashboard-icon-default': theme.palette.dashboard.icons.default,
  '--dashboard-icon-active': theme.palette.dashboard.icons.active,
  '--dashboard-border': theme.palette.dashboard.border,
  '--dashboard-control-background': theme.palette.dashboard.control.background,
  '--dashboard-control-disabled-background': theme.palette.dashboard.control.disabledBackground,
  '--dashboard-menu-background': theme.palette.dashboard.background.secondary,
  '--dashboard-menu-hover-background': theme.palette.dashboard.state.view.background,
  '--dashboard-slider-background': theme.palette.dashboard.background.tertiary
})

const createDashboardCssVariableStyle = (theme: Theme): DashboardCssVariableStyle => ({
  height: '100%',
  ...createDashboardCssVariableMap(theme)
})

const createDashboardComponentOverrides = (theme: Theme): Theme['components'] => {
  const { dashboard, action } = theme.palette
  const cssVarMap = createDashboardCssVariableMap(theme) as Record<string, string>

  const controlStyles = {
    '& .MuiSelect-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-root, & .MuiButton-outlined, & [data-testid="colour-dropdown"] .MuiButton-root':
      {
        backgroundColor: 'var(--dashboard-control-background)',
        color: 'var(--dashboard-text-primary)'
      },
    '& .MuiSelect-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled, & input:not(.MuiInputBase-input):disabled, & textarea:not(.MuiInputBase-input):disabled':
      {
        backgroundColor: 'var(--dashboard-control-disabled-background)',
        color: palette.grey.G8,
        WebkitTextFillColor: palette.grey.G8
      },
    '& .MuiSelect-select.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled, & .MuiInputBase-input.Mui-disabled': {
      color: palette.grey.G8,
      WebkitTextFillColor: palette.grey.G8
    },
    '& .MuiSelect-icon, & .MuiOutlinedInput-root > svg': {
      color: 'var(--dashboard-icon-active)'
    },
    '& .MuiSelect-icon.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled .MuiSelect-icon, & .MuiOutlinedInput-root.Mui-disabled > svg':
      {
        color: palette.grey.G8
      },
    '& .MuiInputLabel-root, & label, & input:not(.MuiInputBase-input):not([data-styles-island] *), & textarea:not(.MuiInputBase-input):not([data-styles-island] *)':
      {
        color: 'var(--dashboard-text-primary)'
      },
    '& .MuiFormControlLabel-root .MuiFormControlLabel-label, & .MuiFormControlLabel-root [class*="MuiFormControlLabel-label"], & [class*="MuiFormControlLabel-root"] [class*="MuiFormControlLabel-label"]':
      {
        color: 'var(--dashboard-text-primary)'
      },
    // shared-components hardcodes palette.black here; this selector adds one extra class for higher specificity
    '& .MuiFormControlLabel-root:has(.MuiCheckbox-root) .MuiFormControlLabel-label': {
      color: 'var(--dashboard-text-primary)'
    },
    '& .MuiFormControlLabel-root:has(.Mui-disabled) .MuiFormControlLabel-label, & .MuiFormControlLabel-root.Mui-disabled .MuiFormControlLabel-label':
      {
        color: palette.grey.G8
      },
    '& .MuiInputBase-input::placeholder, & input:not(.MuiInputBase-input):not([data-styles-island] *)::placeholder, & textarea:not(.MuiInputBase-input):not([data-styles-island] *)::placeholder':
      {
        color: 'var(--dashboard-text-secondary)'
      },
    '& input:not(.MuiInputBase-input):not([data-styles-island] *), & textarea:not(.MuiInputBase-input):not([data-styles-island] *)':
      {
        backgroundColor: 'var(--dashboard-control-background)'
      },
    '& [class*="searchBoxContainer"] [class*="searchField"]': {
      backgroundColor: 'transparent'
    },
    '& .MuiButton-outlined:hover, & .MuiButton-outlined:focus, & .MuiButton-outlined:active': {
      backgroundColor: action.hover
    },
    '& [data-testid="colour-dropdown"] .MuiButton-root:hover, & [data-testid="colour-dropdown"] .MuiButton-root:focus, & [data-testid="colour-dropdown"] .MuiButton-root:active':
      {
        backgroundColor: 'var(--dashboard-control-background)'
      },
    '& .MuiSlider-root, & .MuiSlider-rail, & .MuiSlider-mark, & .MuiSlider-markActive': {
      color: 'var(--dashboard-slider-background)'
    },
    '& .MuiSlider-rail, & .MuiSlider-mark, & .MuiSlider-markActive': {
      backgroundColor: 'var(--dashboard-slider-background)'
    },
    // Bootstrap sets box-sizing: border-box globally; MUI's height: 1.4375em then collapses
    // single-line inputs to ~17px (padding eats the whole box). height: auto restores ~32px.
    '& .MuiOutlinedInput-root .MuiInputBase-input:not(.MuiInputBase-inputMultiline)': {
      height: 'auto'
    }
  }

  const darkDateSelectionStyles =
    theme.palette.mode === 'dark'
      ? {
          '& [class*="MuiTabs-root"], & [role="tablist"]': {
            borderColor: `${dashboard.divider} !important`
          },
          '& [role="tab"]': {
            color: `${dashboard.text.secondary} !important`
          },
          '& [role="tab"][aria-selected="true"]': {
            color: `${dashboard.text.primary} !important`
          },
          '& [data-testid$="-labelled-calendar"]': {
            backgroundColor: `${dashboard.background.tertiary} !important`,
            color: dashboard.text.primary
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiDateCalendar-root"]': {
            color: `${dashboard.text.primary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiButton-root"]': {
            color: `${dashboard.text.primary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiButton-root"] svg': {
            color: `${dashboard.text.primary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiButton-root"]:hover, & [data-testid$="-labelled-calendar"] [class*="MuiIconButton-root"]:hover':
            {
              backgroundColor: `${dashboard.state.view.background} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="MuiPickersDay-root"]:not(.Mui-selected), & [data-testid$="-labelled-calendar"] [class*="MuiPickerDay-root"]:not(.Mui-selected)':
            {
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="MuiPickerDay-root"]:hover, & [data-testid$="-labelled-calendar"] [class*="MuiPickersDay-root"]:hover, & [data-testid$="-labelled-calendar"] [class*="MuiMonthCalendar-button"]:hover, & [data-testid$="-labelled-calendar"] [class*="MuiYearCalendar-button"]:hover':
            {
              backgroundColor: `${dashboard.state.view.background} !important`,
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid^="quarter-selection-"][data-testid$="-calendar"] [class*="MuiMonthCalendar-root"]:has(> button:nth-of-type(n + 1):nth-of-type(-n + 3):is(:hover, :focus)) > button:nth-of-type(-n + 3), & [data-testid^="quarter-selection-"][data-testid$="-calendar"] [class*="MuiMonthCalendar-root"]:has(> button:nth-of-type(n + 4):nth-of-type(-n + 6):is(:hover, :focus)) > button:nth-of-type(n + 4):nth-of-type(-n + 6), & [data-testid^="quarter-selection-"][data-testid$="-calendar"] [class*="MuiMonthCalendar-root"]:has(> button:nth-of-type(n + 7):nth-of-type(-n + 9):is(:hover, :focus)) > button:nth-of-type(n + 7):nth-of-type(-n + 9), & [data-testid^="quarter-selection-"][data-testid$="-calendar"] [class*="MuiMonthCalendar-root"]:has(> button:nth-of-type(n + 10):nth-of-type(-n + 12):is(:hover, :focus)) > button:nth-of-type(n + 10):nth-of-type(-n + 12)':
            {
              backgroundColor: `${dashboard.state.view.background} !important`,
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="MuiPickersDay-dayOutsideMonth"], & [data-testid$="-labelled-calendar"] [class*="MuiPickerDay-dayOutsideMonth"]':
            {
              color: `${palette.grey.G8} !important`
            },
          '& [data-testid$="-labelled-calendar"] span[style*="color"]': {
            color: `${dashboard.text.secondary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiDayCalendar-weekDayLabel"]': {
            color: `${dashboard.text.secondary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiDayCalendar-weekNumberLabel"]': {
            color: `${dashboard.text.secondary} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="MuiDayCalendar-weekNumber"]': {
            color: `${dashboard.text.primary} !important`
          },
          '& [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button:hover) button[class*="MuiPickerDay-root"], & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button:hover) button[class*="MuiPickersDay-root"], & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button:focus) button[class*="MuiPickerDay-root"], & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button:focus) button[class*="MuiPickersDay-root"]':
            {
              backgroundColor: `${dashboard.state.view.background} !important`,
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickerDay-root"], & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickersDay-root"]':
            {
              backgroundColor: `${dashboard.state.view.background} !important`,
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickerDay-root"]:hover, & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickerDay-root"]:focus, & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickersDay-root"]:hover, & [data-testid^="week-selection-"][data-testid$="-calendar"] [class*="MuiDayCalendar-weekContainer"]:has(button[aria-selected="true"]) button[class*="MuiPickersDay-root"]:focus':
            {
              backgroundColor: `${dashboard.state.view.background} !important`
            },
          '& [data-testid$="-labelled-calendar"] [data-testid$="-offset-value-label"], & [data-testid$="-labelled-calendar"] [data-testid$="-offset-increment-label"]':
            {
              color: `${dashboard.text.secondary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="MuiDivider-root"]': {
            borderColor: dashboard.divider
          },
          '& [data-testid$="-labelled-calendar"] [class*="offsetTextField"], & [data-testid$="-labelled-calendar"] [class*="stepperContainer"], & [data-testid$="-labelled-calendar"] [class*="styledToggleButton"]':
            {
              backgroundColor: `${dashboard.control.background} !important`,
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="stepperContainer"]': {
            borderColor: `${dashboard.divider} !important`
          },
          '& [data-testid$="-labelled-calendar"] [class*="stepperContainer"] button, & [data-testid$="-labelled-calendar"] [class*="stepperContainer"] svg':
            {
              color: `${dashboard.text.primary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [data-testid$="-toggle-input-type-btn"], & [data-testid$="-labelled-calendar"] [data-testid$="-toggle-input-type-btn"] svg':
            {
              color: `${dashboard.text.secondary} !important`
            },
          '& [data-testid$="-labelled-calendar"] [class*="styledToggleButton"].Mui-selected': {
            borderColor: `${dashboard.divider} !important`
          }
        }
      : {}

  const menuPaperStyles = {
    ...cssVarMap,
    backgroundColor: `${dashboard.background.secondary} !important`,
    backgroundImage: 'none !important',
    color: dashboard.text.primary,
    border: `1px solid ${dashboard.border}`,
    '& [class*="MuiMenuItem"]': {
      color: `${dashboard.text.primary} !important`
    },
    '& [class*="MuiMenuItem"]:hover': {
      backgroundColor: `${dashboard.state.view.background} !important`
    },
    '& [role="heading"]': { color: dashboard.text.secondary },
    '& .MuiList-root > div': { color: dashboard.text.secondary }
  }

  const darkCheckboxStyles =
    theme.palette.mode === 'dark'
      ? {
          [`& .MuiCheckbox-root:not(.Mui-checked):not(.MuiCheckbox-indeterminate):not([data-dashboard-theme='light'] *)`]:
            {
              '--fill-color': dashboard.control.background,
              '--stroke-color': palette.white,
              '--mark-fill-color': palette.white
            }
        }
      : {}

  const darkSubscriptionStyles =
    theme.palette.mode === 'dark'
      ? {
          '& [data-testid="subscription-schedule-draft"]': {
            backgroundColor: `${dashboard.background.subtle} !important`
          },
          '& [data-testid="semaphore-upsert-subscription-dialog-content"] div:has(> .MuiTypography-root + div > button[aria-label^="Delete "])':
            {
              backgroundColor: `${dashboard.background.subtle} !important`,
              '& > .MuiTypography-root, & .MuiIconButton-root': {
                color: dashboard.text.primary
              }
            },
          '& [data-testid="semaphore-create-subscription-dialog-title"]': {
            borderBottom: `1px solid ${palette.grey.G10}`
          },
          '& [data-testid="semaphore-create-subscription-dialog-actions"]': {
            borderTop: `1px solid ${palette.grey.G10}`
          }
        }
      : {}

  const dialogPaperStyles = {
    ...cssVarMap,
    ...controlStyles,
    backgroundColor: `${dashboard.background.secondary} !important`,
    backgroundImage: 'none !important',
    color: dashboard.text.primary,
    border: `1px solid ${dashboard.border}`,
    outline: 0,
    ...darkCheckboxStyles,
    ...darkSubscriptionStyles
  }

  return {
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          color: 'var(--dashboard-text-primary)',
          ...controlStyles,
          ...darkCheckboxStyles,
          ...darkDateSelectionStyles
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          ...menuPaperStyles,
          ...controlStyles,
          ...darkCheckboxStyles
        } as Record<string, unknown>
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: menuPaperStyles as Record<string, unknown>
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          ...menuPaperStyles,
          ...controlStyles
        } as Record<string, unknown>,
        option: {
          '&:hover': { backgroundColor: dashboard.state.view.background },
          '&.Mui-focused': { backgroundColor: dashboard.state.view.background }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiDialog-paper': dialogPaperStyles,
          '&:not(.MuiDrawer-paper):has([data-testid^="dropdown-menu-item-"])': menuPaperStyles,
          '&:not(.MuiDrawer-paper):has(.MuiMenuList-root)': menuPaperStyles
        } as Record<string, unknown>
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: dialogPaperStyles as Record<string, unknown>
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { color: `${dashboard.text.primary} !important` }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { color: `${dashboard.text.primary} !important` },
        dividers: {
          borderTopColor: dashboard.border,
          borderBottomColor: dashboard.border
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: { color: `${dashboard.text.primary} !important` }
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: darkCheckboxStyles as Record<string, unknown>
      }
    }
  }
}

export const createAppTheme = (mode: DashboardThemeMode = 'light') => {
  const defaultTheme = createTheme(themeConfig)

  const baseTheme = createTheme({
    ...themeConfig,
    palette: {
      ...defaultTheme.palette,
      mode,
      dashboard: createDefaultDashboardPalette(mode)
    }
  })

  return createTheme(baseTheme, {
    components: createDashboardComponentOverrides(baseTheme)
  })
}

/**
 * Inline CSS variables for a given dashboard mode. Useful when a portaled element
 * (e.g. a Select menu's popover) escapes the StylesProvider scope and needs to
 * pin the dashboard tokens regardless of which Global stylesheet wins.
 */
export const dashboardModeCssVariables = (mode: DashboardThemeMode): CSSProperties =>
  createDashboardCssVariableMap(createAppTheme(mode)) as CSSProperties

const createGlobalStyles = (theme: Theme) => css`
  body {
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize}px;
    margin: 0;
    user-select: none;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    // Reset the bootstrap styles injected by core
    line-height: unset;
  }

  [data-testid="proxy-page"] {
    // Set custom CSS variable for when mounted on Federate API Proxy Page
    --top-navigation-bar-height: 48px;
  }

  .${scopedCssBaselineClasses.root} {
    height: 100%;
  }

  body > .MuiPopper-root {
    z-index: ${theme.zIndex.modal};
  }

  body .notistack-SnackbarContainer {
    z-index: ${theme.zIndex.snackbar};
  }

  .highcharts-container {
    border-radius: 8px;
  }
`

function StylesProviderInner({ children, includeGlobalStyles }: { children: ReactNode; includeGlobalStyles: boolean }) {
  const { mode } = useTheme()
  const theme = createAppTheme(mode)
  const globalStyles = createGlobalStyles(theme)
  const dashboardCssVariableStyle = createDashboardCssVariableStyle(theme)

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {includeGlobalStyles && <Global styles={globalStyles} />}
        <ScopedCssBaseline data-dashboard-theme={mode} style={dashboardCssVariableStyle}>
          {children}
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export function StylesProvider({
  children,
  mode = 'light',
  includeGlobalStyles = true
}: Readonly<StylesProviderProps>) {
  return (
    <AppThemeProvider initialMode={mode}>
      <StylesProviderInner includeGlobalStyles={includeGlobalStyles}>{children}</StylesProviderInner>
    </AppThemeProvider>
  )
}
